import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { communityReportsApi, testSessionsApi } from '../../api/services';
import { Skeleton } from '../../components/Feedback/Skeleton';
import { PageTransition } from '../../components/Transitions/PageTransition';
import { ConfidenceBadge } from '../../components/Trust/ConfidenceBadge';
import { StalenessBadge } from '../../components/Trust/StalenessBadge';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { cn } from '../../lib/cn';
import { formatDate, formatNumber } from '../../lib/format';
import type { CommunityReport, LanguageTestType, SeatStatus, TestSeatContent, TestSession } from '../../types';

const TEST_TYPES: LanguageTestType[] = ['ielts', 'toefl_ibt', 'pte', 'duolingo', 'gre', 'goethe', 'testdaf', 'tcf', 'tef', 'delf'];

// Canonical keys keep "Tehran" and "تهران" in one subject; labels are translated.
const CITIES = ['Tehran', 'Mashhad', 'Isfahan', 'Shiraz', 'Tabriz', 'Karaj', 'Ahvaz', 'Qom', 'Kerman', 'Rasht', 'Kish'];

const OFFICIAL_SITES: Partial<Record<LanguageTestType, string>> = {
  ielts: 'https://ielts.org',
  toefl_ibt: 'https://www.ets.org/toefl.html',
  pte: 'https://www.pearsonpte.com',
  duolingo: 'https://englishtest.duolingo.com',
  gre: 'https://www.ets.org/gre.html',
  goethe: 'https://www.goethe.de',
  testdaf: 'https://www.testdaf.de',
  tcf: 'https://www.france-education-international.fr',
  delf: 'https://www.france-education-international.fr',
  tef: 'https://www.lefrancaisdesaffaires.fr',
};

const SEAT_TONE: Record<SeatStatus, string> = {
  available: 'bg-status-success/10 text-emerald-700 dark:text-emerald-300',
  limited: 'bg-status-warning/15 text-amber-800 dark:text-amber-300',
  full: 'bg-status-danger/10 text-red-700 dark:text-red-300',
};

const OBSERVED_OFFSETS_HOURS = { now: 0, today: 4, yesterday: 24, days: 60 } as const;
type ObservedWhen = keyof typeof OBSERVED_OFFSETS_HOURS;

const cityKey = (city: string) => city.trim().toLowerCase();

export function LanguageTestsPage() {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const { isAuthenticated } = useAuth();

  const [testType, setTestType] = useState<LanguageTestType>('ielts');
  const [city, setCity] = useState('');
  const [reports, setReports] = useState<CommunityReport[]>([]);
  const [sessions, setSessions] = useState<TestSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    Promise.all([
      communityReportsApi.list({ report_type: 'test_seat_availability', subject_prefix: `${testType}:`, include_stale: true }),
      testSessionsApi.list({ test_type: testType, city: city || undefined }),
    ])
      .then(([reportRows, sessionRows]) => {
        if (cancelled) return;
        setReports(reportRows);
        setSessions(sessionRows);
      })
      .catch((err) => {
        console.error('Failed to load test status:', err);
        if (!cancelled) setLoadError(true);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [testType, city, reload]);

  const cityLabel = (name: string) => t(`languageTests.cities.${name}`, { defaultValue: name });

  // Latest report per city (reports arrive newest first), plus how many recent ones exist.
  const byCity = useMemo(() => {
    const groups = new Map<string, { latest: CommunityReport; count: number }>();
    for (const report of reports) {
      const key = cityKey(String(report.content.city ?? ''));
      if (city && key !== cityKey(city)) continue;
      const group = groups.get(key);
      if (group) group.count += 1;
      else groups.set(key, { latest: report, count: 1 });
    }
    return [...groups.values()];
  }, [reports, city]);

  return (
    <PageTransition>
      <div className="mx-auto max-w-5xl px-4 py-8 space-y-8">
        <header className="space-y-2">
          <h1 className="text-2xl font-bold text-text-primary [text-wrap:balance]">{t('languageTests.title')}</h1>
          <p className="max-w-[65ch] text-text-secondary [text-wrap:pretty]">{t('languageTests.subtitle')}</p>
        </header>

        <div className="space-y-4">
          <div role="radiogroup" aria-label={t('languageTests.testType')} className="flex flex-wrap gap-2">
            {TEST_TYPES.map((type) => (
              <button
                key={type}
                role="radio"
                aria-checked={testType === type}
                onClick={() => setTestType(type)}
                className={cn(
                  'rounded-full px-3 py-1 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2',
                  testType === type
                    ? 'bg-brand-primary text-white'
                    : 'border border-border bg-elevated text-text-secondary hover:text-text-primary'
                )}
              >
                {t(`languageTests.tests.${type}`)}
              </button>
            ))}
          </div>
          <label className="flex max-w-xs flex-col gap-1.5 text-sm font-medium text-text-secondary">
            {t('languageTests.city')}
            <select
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="rounded-lg border border-border bg-surface px-3 py-2.5 text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
            >
              <option value="">{t('languageTests.allCities')}</option>
              {CITIES.map((name) => (
                <option key={name} value={name}>{cityLabel(name)}</option>
              ))}
            </select>
          </label>
        </div>

        {loadError && (
          <div className="rounded-xl border border-status-danger/30 bg-status-danger/5 px-4 py-3 text-sm text-text-primary">
            {t('languageTests.loadError')}{' '}
            <button onClick={() => setReload((n) => n + 1)} className="font-medium text-brand-primary underline underline-offset-2">
              {t('common.retry', 'Retry')}
            </button>
          </div>
        )}

        <section aria-labelledby="seats-heading" className="space-y-3">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 id="seats-heading" className="text-lg font-semibold text-text-primary">{t('languageTests.seats.title')}</h2>
              <p className="mt-1 max-w-[65ch] text-sm text-text-secondary">{t('languageTests.seats.disclaimer')}</p>
            </div>
            {isAuthenticated ? (
              <button
                onClick={() => setFormOpen((open) => !open)}
                aria-expanded={formOpen}
                className="rounded-lg border border-brand-primary px-4 py-2 text-sm font-medium text-brand-primary hover:bg-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
              >
                {formOpen ? t('common.cancel') : t('languageTests.report.open')}
              </button>
            ) : (
              <Link
                to="/login"
                state={{ from: '/language-tests' }}
                className="rounded-lg border border-brand-primary px-4 py-2 text-sm font-medium text-brand-primary hover:bg-elevated"
              >
                {t('languageTests.report.signIn')}
              </Link>
            )}
          </div>

          <AnimatePresence initial={false}>
            {formOpen && (
              <SeatReportForm
                testType={testType}
                defaultCity={city}
                onDone={() => {
                  setFormOpen(false);
                  setReload((n) => n + 1);
                }}
              />
            )}
          </AnimatePresence>

          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-16 rounded-xl" />
              <Skeleton className="h-16 rounded-xl" />
            </div>
          ) : byCity.length === 0 ? (
            <p className="rounded-xl border border-dashed border-border bg-surface px-4 py-6 text-sm text-text-secondary">
              {t('languageTests.seats.empty', { test: t(`languageTests.tests.${testType}`) })}
            </p>
          ) : (
            <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-surface">
              {byCity.map(({ latest, count }) => (
                <SeatRow key={latest.id} report={latest} recentCount={count} cityLabel={cityLabel} />
              ))}
            </ul>
          )}
        </section>

        <section aria-labelledby="sessions-heading" className="space-y-3">
          <div>
            <h2 id="sessions-heading" className="text-lg font-semibold text-text-primary">{t('languageTests.sessions.title')}</h2>
            <p className="mt-1 max-w-[65ch] text-sm text-text-secondary">{t('languageTests.sessions.subtitle')}</p>
          </div>
          {loading ? (
            <Skeleton className="h-32 rounded-xl" />
          ) : sessions.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-surface px-4 py-6 text-sm text-text-secondary">
              {t('languageTests.sessions.empty')}
              {OFFICIAL_SITES[testType] && (
                <>
                  {' '}
                  <a href={OFFICIAL_SITES[testType]} target="_blank" rel="noopener noreferrer" className="font-medium text-brand-primary underline underline-offset-2">
                    {t('languageTests.sessions.officialSite', { test: t(`languageTests.tests.${testType}`) })}
                  </a>
                </>
              )}
            </div>
          ) : (
            <>
            {/* Phones: one stacked entry per session, so price and source stay visible. */}
            <ul className="divide-y divide-border rounded-xl border border-border bg-surface md:hidden">
              {sessions.map((s) => (
                <li key={s.id} className="space-y-1 px-4 py-3 text-sm">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="font-medium text-text-primary">
                      {formatDate(s.session_date, language, { year: 'numeric', month: 'short', day: 'numeric' })}
                      {s.format && <span className="ms-2 text-xs font-normal text-text-muted">{t(`languageTests.formats.${s.format}`)}</span>}
                    </span>
                    <span className="text-text-secondary">{cityLabel(s.city)}</span>
                  </div>
                  <p className="text-text-secondary">
                    {[
                      s.test_center,
                      s.price_irr && t('languageTests.toman', { amount: formatNumber(Math.round(s.price_irr / 10), language) }),
                      s.registration_deadline &&
                        `${t('languageTests.sessions.columns.deadline')}: ${formatDate(s.registration_deadline, language, { month: 'short', day: 'numeric' })}`,
                    ]
                      .filter(Boolean)
                      .join(language === 'fa' ? '، ' : ' · ')}
                  </p>
                  <p className="text-xs text-text-muted">
                    {s.source_url ? (
                      <a href={s.source_url} target="_blank" rel="noopener noreferrer" className="text-brand-primary underline underline-offset-2">
                        {t(`languageTests.sources.${s.source}`)}
                      </a>
                    ) : (
                      t(`languageTests.sources.${s.source}`)
                    )}{' '}
                    {t('languageTests.sessions.checked', { date: formatDate(s.last_checked_at, language, { month: 'short', day: 'numeric' }) })}
                  </p>
                </li>
              ))}
            </ul>
            <div className="hidden overflow-x-auto rounded-xl border border-border bg-surface md:block">
              <table className="w-full min-w-[640px] text-sm">
                <thead className="bg-elevated text-start text-text-secondary">
                  <tr>
                    {(['date', 'city', 'center', 'deadline', 'price', 'source'] as const).map((col) => (
                      <th key={col} scope="col" className="px-4 py-2.5 text-start font-medium">
                        {t(`languageTests.sessions.columns.${col}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {sessions.map((s) => (
                    <tr key={s.id}>
                      <td className="px-4 py-3 font-medium text-text-primary whitespace-nowrap">
                        {formatDate(s.session_date, language, { year: 'numeric', month: 'short', day: 'numeric' })}
                        {s.format && <span className="ms-2 text-xs font-normal text-text-muted">{t(`languageTests.formats.${s.format}`)}</span>}
                      </td>
                      <td className="px-4 py-3 text-text-secondary">{cityLabel(s.city)}</td>
                      <td className="px-4 py-3 text-text-secondary">{s.test_center ?? '—'}</td>
                      <td className="px-4 py-3 text-text-secondary whitespace-nowrap">
                        {s.registration_deadline
                          ? formatDate(s.registration_deadline, language, { month: 'short', day: 'numeric' })
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-text-secondary whitespace-nowrap">
                        {s.price_irr ? t('languageTests.toman', { amount: formatNumber(Math.round(s.price_irr / 10), language) }) : '—'}
                      </td>
                      <td className="px-4 py-3">
                        {s.source_url ? (
                          <a href={s.source_url} target="_blank" rel="noopener noreferrer" className="text-brand-primary underline underline-offset-2">
                            {t(`languageTests.sources.${s.source}`)}
                          </a>
                        ) : (
                          <span className="text-text-muted">{t(`languageTests.sources.${s.source}`)}</span>
                        )}
                        <span className="block text-xs text-text-muted">
                          {t('languageTests.sessions.checked', {
                            date: formatDate(s.last_checked_at, language, { month: 'short', day: 'numeric' }),
                          })}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </section>
      </div>
    </PageTransition>
  );
}

function SeatRow({
  report,
  recentCount,
  cityLabel,
}: {
  report: CommunityReport;
  recentCount: number;
  cityLabel: (name: string) => string;
}) {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const content = report.content as unknown as TestSeatContent;

  return (
    <li className={cn('flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between', report.staleness === 'stale' && 'opacity-75')}>
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-text-primary">{cityLabel(content.city)}</span>
          <span className={cn('rounded-full px-2 py-0.5 text-xs font-semibold', SEAT_TONE[content.seats])}>
            {t(`languageTests.seats.status.${content.seats}`)}
          </span>
        </div>
        <p className="text-xs text-text-muted">
          {[
            content.test_date && t('languageTests.seats.forDate', {
              date: formatDate(content.test_date, language, { month: 'short', day: 'numeric' }),
            }),
            content.test_center,
            content.price_irr && t('languageTests.toman', { amount: formatNumber(Math.round(content.price_irr / 10), language) }),
            recentCount > 1 && t('languageTests.seats.recentCount', { count: recentCount, n: formatNumber(recentCount, language) }),
          ]
            .filter(Boolean)
            // "·" reads as the Persian zero "۰" next to digits; use the Persian comma there.
            .join(language === 'fa' ? '، ' : ' · ')}
        </p>
        {content.note && <p className="text-sm text-text-secondary">{content.note}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2 sm:justify-end">
        <StalenessBadge staleness={report.staleness} ageHours={report.age_hours} />
        <ConfidenceBadge confidence={report.confidence} corroborations={report.corroboration_count} />
      </div>
    </li>
  );
}

function SeatReportForm({ testType, defaultCity, onDone }: { testType: LanguageTestType; defaultCity: string; onDone: () => void }) {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  const [city, setCity] = useState(defaultCity || 'Tehran');
  const [otherCity, setOtherCity] = useState('');
  const [seats, setSeats] = useState<SeatStatus | ''>('');
  const [testDate, setTestDate] = useState('');
  const [center, setCenter] = useState('');
  const [priceToman, setPriceToman] = useState('');
  const [observed, setObserved] = useState<ObservedWhen>('now');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const finalCity = city === 'other' ? otherCity.trim() : city;
  const valid = seats !== '' && finalCity.length >= 2;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!valid) {
      setShowErrors(true);
      return;
    }
    setSubmitting(true);
    try {
      const content: Record<string, unknown> = { test_type: testType, city: finalCity, seats };
      if (testDate) content.test_date = testDate;
      if (center.trim()) content.test_center = center.trim();
      if (priceToman) content.price_irr = Number(priceToman) * 10;
      if (note.trim()) content.note = note.trim();
      const observedAt = new Date(Date.now() - OBSERVED_OFFSETS_HOURS[observed] * 3600_000).toISOString();
      await communityReportsApi.submit({ report_type: 'test_seat_availability', content, observed_at: observedAt });
      toast.success(t('languageTests.report.thanks'));
      onDone();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('languageTests.report.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const field = 'w-full rounded-lg border border-border bg-surface px-3.5 py-2.5 text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary';

  return (
    <motion.form
      onSubmit={submit}
      initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18, ease: [0.25, 1, 0.5, 1] }}
      className="space-y-4 rounded-xl border border-border bg-surface p-5"
      noValidate
    >
      <fieldset>
        <legend className="mb-2 text-sm font-medium text-text-primary">
          {t('languageTests.report.seatsQuestion', { test: t(`languageTests.tests.${testType}`) })}
        </legend>
        <div className="flex flex-wrap gap-2">
          {(['available', 'limited', 'full'] as SeatStatus[]).map((value) => (
            <label
              key={value}
              className={cn(
                'cursor-pointer rounded-lg border px-4 py-2 text-sm font-medium transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-brand-primary',
                seats === value ? 'border-brand-primary bg-brand-primary/10 text-brand-primary' : 'border-border text-text-secondary hover:bg-elevated'
              )}
            >
              <input type="radio" name="seats" value={value} checked={seats === value} onChange={() => setSeats(value)} className="sr-only" />
              {t(`languageTests.seats.status.${value}`)}
            </label>
          ))}
        </div>
        {showErrors && seats === '' && <p className="mt-1.5 text-xs text-status-danger">{t('languageTests.report.pickSeats')}</p>}
      </fieldset>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="space-y-1.5 text-sm font-medium text-text-secondary">
          <span>{t('languageTests.city')}</span>
          <select value={city} onChange={(e) => setCity(e.target.value)} className={field}>
            {CITIES.map((name) => (
              <option key={name} value={name}>{t(`languageTests.cities.${name}`)}</option>
            ))}
            <option value="other">{t('languageTests.otherCity')}</option>
          </select>
        </label>
        {city === 'other' && (
          <label className="space-y-1.5 text-sm font-medium text-text-secondary">
            <span>{t('languageTests.otherCityName')}</span>
            <input value={otherCity} onChange={(e) => setOtherCity(e.target.value)} maxLength={80} className={field} />
            {showErrors && finalCity.length < 2 && <span className="text-xs text-status-danger">{t('languageTests.report.pickCity')}</span>}
          </label>
        )}
        <label className="space-y-1.5 text-sm font-medium text-text-secondary">
          <span>{t('languageTests.report.when')}</span>
          <select value={observed} onChange={(e) => setObserved(e.target.value as ObservedWhen)} className={field}>
            {(Object.keys(OBSERVED_OFFSETS_HOURS) as ObservedWhen[]).map((key) => (
              <option key={key} value={key}>{t(`languageTests.report.observed.${key}`)}</option>
            ))}
          </select>
        </label>
        <label className="space-y-1.5 text-sm font-medium text-text-secondary">
          <span>{t('languageTests.report.testDate')} <span className="font-normal text-text-muted">{t('common.optional', '(optional)')}</span></span>
          <input type="date" value={testDate} onChange={(e) => setTestDate(e.target.value)} className={field} />
        </label>
        <label className="space-y-1.5 text-sm font-medium text-text-secondary">
          <span>{t('languageTests.report.center')} <span className="font-normal text-text-muted">{t('common.optional', '(optional)')}</span></span>
          <input value={center} onChange={(e) => setCenter(e.target.value)} maxLength={120} className={field} />
        </label>
        <label className="space-y-1.5 text-sm font-medium text-text-secondary">
          <span>{t('languageTests.report.price')} <span className="font-normal text-text-muted">{t('common.optional', '(optional)')}</span></span>
          <input
            inputMode="numeric"
            value={priceToman}
            onChange={(e) => setPriceToman(e.target.value.replace(/[^\d]/g, ''))}
            className={field}
          />
        </label>
      </div>

      <label className="block space-y-1.5 text-sm font-medium text-text-secondary">
        <span>{t('languageTests.report.note')} <span className="font-normal text-text-muted">{t('languageTests.report.noteHint')}</span></span>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} rows={2} className={field} />
      </label>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-[60ch] text-xs text-text-muted">{t('languageTests.report.privacy')}</p>
        <button
          type="submit"
          disabled={submitting}
          className="rounded-lg bg-brand-primary px-5 py-2.5 text-sm font-medium text-white hover:bg-brand-secondary disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
        >
          {submitting ? t('common.saving') : t('languageTests.report.submit')}
        </button>
      </div>
    </motion.form>
  );
}
